// HowItWorksStrip — compact 4-step explainer rendered near the top of
// the on-page booking widget. Fully self-contained (own copy strings,
// no external state), so it can be reused on landing pages or dedicated
// booking flows without dragging the whole widget along.

import React from "react";
import {
  BedDouble, Send, ClipboardCheck, BadgeCheck, ChevronRight, Info,
} from "lucide-react";

const pick = (obj, lang) => (obj?.[lang] ?? obj?.en ?? "");

const COPY = {
  overline: { en: "How it works", fr: "Comment ça marche", es: "Cómo funciona" },
  s1_title: { en: "Choose your stay", fr: "Choisissez votre séjour", es: "Elige tu estancia" },
  s1_body:  { en: "Pick a hotel, desert camp or mountain lodge.", fr: "Hôtel, camp du désert ou refuge de montagne.", es: "Hotel, campamento o refugio de montaña." },
  s2_title: { en: "Submit your request", fr: "Envoyez votre demande", es: "Envía tu solicitud" },
  s2_body:  { en: "Tell us dates, travellers and preferences.", fr: "Dates, voyageurs et préférences.", es: "Fechas, viajeros y preferencias." },
  s3_title: { en: "Availability review", fr: "Étude de disponibilité", es: "Revisión de disponibilidad" },
  s3_body:  { en: "Our team prepares a tailored proposal.", fr: "Notre équipe prépare une proposition sur mesure.", es: "Nuestro equipo prepara una propuesta a medida." },
  s4_title: { en: "Confirm your reservation", fr: "Confirmez votre réservation", es: "Confirma tu reserva" },
  s4_body:  { en: "Review, confirm and receive your booking.", fr: "Validez et recevez votre confirmation.", es: "Valida y recibe tu confirmación." },
  note: {
    en: "This is an enquiry, not an instant booking — a host reviews availability and follows up with a tailored quote within 24 hours.",
    fr: "Il s'agit d'une demande, non d'une réservation instantanée — un hôte étudie la disponibilité et vous propose un devis sur mesure sous 24 heures.",
    es: "Se trata de una solicitud, no de una reserva instantánea — un anfitrión revisa la disponibilidad y te envía una propuesta a medida en 24 horas.",
  },
};

const STEPS = [
  { key: "s1", icon: BedDouble },
  { key: "s2", icon: Send },
  { key: "s3", icon: ClipboardCheck },
  { key: "s4", icon: BadgeCheck },
];

export default function HowItWorksStrip({ lang }) {
  return (
    <div
      className="mb-5 md:mb-6 -mt-1 md:-mt-2 p-4 md:p-5 bg-[#FDF8EE]/70 border border-[#D4A373]/35"
      data-testid="home-booking-how-it-works"
    >
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#A07042] mb-3">
        {pick(COPY.overline, lang)}
      </p>
      <ol className="grid grid-cols-2 md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-x-2 md:gap-x-3 gap-y-4 items-start">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          const isLast = i === STEPS.length - 1;
          return (
            <React.Fragment key={step.key}>
              <li
                className="flex items-start gap-2.5 min-w-0"
                data-testid={`home-booking-how-it-works-step-${i + 1}`}
              >
                <span className="inline-flex items-center justify-center w-8 h-8 shrink-0 bg-white border border-[#D4A373]/45 text-[#C16542] relative">
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
                  <span className="absolute -top-1.5 -left-1.5 inline-flex items-center justify-center w-4 h-4 bg-[#C16542] text-[#FDFBF7] text-[9px] tabular-nums font-medium">
                    {i + 1}
                  </span>
                </span>
                <span className="flex flex-col min-w-0">
                  <span className="text-[11px] tracking-[0.18em] uppercase text-[#2C2621] leading-tight break-words">
                    {pick(COPY[`${step.key}_title`], lang)}
                  </span>
                  <span className="text-[11px] text-[#5C5248] leading-snug mt-0.5">
                    {pick(COPY[`${step.key}_body`], lang)}
                  </span>
                </span>
              </li>
              {!isLast ? (
                <span
                  aria-hidden="true"
                  className="hidden md:flex items-center justify-center text-[#D4A373]/70 pt-2"
                >
                  <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.6} />
                </span>
              ) : null}
            </React.Fragment>
          );
        })}
      </ol>
      <p
        className="mt-4 pt-3 border-t border-[#D4A373]/30 text-[11px] leading-relaxed text-[#5C5248] flex items-start gap-2"
        data-testid="home-booking-how-it-works-note"
      >
        <Info className="w-3 h-3 mt-0.5 shrink-0 text-[#A07042]" strokeWidth={1.7} />
        <span>{pick(COPY.note, lang)}</span>
      </p>
    </div>
  );
}
